import { useState, useEffect, useCallback } from 'react'
import './GestorPlantillas.css'

// 🚀 Constante global declarada fuera del componente
const BACKEND_URL = process.env.REACT_APP_API_URL

export default function GestorPlantillas({ toast, comercioId }) {
  const [plantillas, setPlantillas] = useState([])
  const [cargando, setCargando] = useState(false)
  
  const [editId, setEditId] = useState(null) 
  const [nombre, setNombre] = useState('')
  const [diasEspera, setDiasEspera] = useState(3)
  const [texto, setTexto] = useState('')

  const cargarPlantillas = useCallback(async () => {
    if (!comercioId) return
    setCargando(true)
    try {
        const res = await fetch(`${BACKEND_URL}/api/plantillas/${comercioId}`)
      if (res.ok) {
        const data = await res.json()
        const dataOrdenada = data.sort((a, b) => a.id - b.id)
        setPlantillas(dataOrdenada)
      }
    } catch (error) {
      console.error(error)
      toast('Error al cargar las plantillas', 'error')
    } finally {
      setCargando(false)
    }
  }, [comercioId, toast])

  useEffect(() => {
    cargarPlantillas()
  }, [cargarPlantillas])

  const handleIniciarEdicion = (plantilla) => {
    setEditId(plantilla.id)
    setNombre(plantilla.nombre)
    setDiasEspera(plantilla.dias_espera)
    setTexto(plantilla.texto)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleCancelarEdicion = () => {
    setEditId(null)
    setNombre('')
    setDiasEspera(3)
    setTexto('')
  }

  const handleGuardarPlantilla = async (e) => {
    e.preventDefault()

    if (!nombre || !texto) {
      toast('Por favor, completa todos los campos', 'warning')
      return
    }

    try {
      const url = editId ? `${BACKEND_URL}/api/plantillas/${editId}` : `${BACKEND_URL}/api/plantillas`
      const metodo = editId ? 'PUT' : 'POST'

      const res = await fetch(url, {
        method: metodo,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          comercio_id: parseInt(comercioId, 10), 
          nombre: nombre,
          dias_espera: parseInt(diasEspera, 10), 
          texto: texto
        })
      })

      if (res.ok) {
        toast(editId ? '¡Estrategia actualizada correctamente!' : '¡Plantilla creada exitosamente!', 'success')
        handleCancelarEdicion()
        cargarPlantillas() 
      } else {
        toast('Error al guardar la plantilla', 'error')
      }
    } catch (error) {
      toast('Error de conexión con el servidor', 'error')
    }
  }

  const handleEliminarPlantilla = async (id) => {
    if (!window.confirm('¿Estás seguro de que querés eliminar esta estrategia?')) return

    try {
      const res = await fetch(`${BACKEND_URL}/api/plantillas/${id}`, {
        method: 'DELETE'
      })

      if (res.ok) {
        toast('Plantilla eliminada', 'success')
        if (editId === id) handleCancelarEdicion()
        cargarPlantillas()
      } else {
        toast('No se pudo eliminar la plantilla', 'error')
      }
    } catch (error) {
      toast('Error de conexión', 'error')
    }
  }

  return (
    <div className="gp-wrapper">
      <div className="gp-grid">
        
        {/* FORMULARIO DE CREACIÓN / EDICIÓN */}
        <div className="gp-card gp-form-box">
          <h3>{editId ? '📝 Editar Estrategia Post-Venta' : '➕ Nueva Estrategia Post-Venta'}</h3>
          <p className="gp-help-text">Definí cómo y cuándo querés contactar a tus clientes automáticamente.</p>
          
          <form onSubmit={handleGuardarPlantilla}>
            <div className="gp-form-group">
              <label>Nombre de la Estrategia:</label>
              <input 
                type="text" 
                placeholder="Ej: Satisfacción Corta"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
              />
            </div>

            <div className="gp-form-group">
              <label>Días de espera post-venta:</label>
              <input 
                type="number" 
                value={diasEspera}
                onChange={(e) => setDiasEspera(e.target.value)}
              />
            </div>

            <div className="gp-form-group">
              <label>Mensaje de WhatsApp:</label>
              <textarea 
                rows="5"
                placeholder="¡Hola {nombre}! ¿Cómo va tu {equipo}?..."
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
              />
              <div className="gp-tags-guide">
                <span>💡 Etiquetas mágicas:</span>
                <ul>
                  <li><code>{"{nombre}"}</code> ➔ Primer nombre del cliente.</li>
                  <li><code>{"{equipo}"}</code> ➔ Modelo del celular.</li>
                </ul>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="btn-gp-submit" style={{ flex: 1 }}>
                {editId ? '💾 Actualizar Estrategia' : 'Guardar Estrategia'}
              </button>
              {editId && (
                <button type="button" onClick={handleCancelarEdicion} style={{ padding: '0 15px', backgroundColor: '#475569', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                  Cancelar
                </button>
              )}
            </div>
          </form>
        </div>

        {/* LISTADO DE PLANTILLAS ACTIVAS */}
        <div className="gp-card gp-list-box">
          <h3 style={{ marginBottom: '1.5rem' }}>Mis Estrategias Activas</h3>
          {cargando ? (
            <div className="gp-loader">Sincronizando plantillas...</div>
          ) : plantillas.length === 0 ? (
            <div className="gp-empty">
              <p>No tenés estrategias personalizadas creadas todavía.</p>
            </div>
          ) : (
            <div className="gp-list-grid" style={{ display: 'grid', gap: '1.5rem' }}>
              {plantillas.map((p) => (
                <div key={p.id} className="gp-item-card">
                  <div className="gp-item-header">
                    <h4>{p.nombre}</h4>
                    <span className="gp-days-badge">A los {p.dias_espera} días</span>
                  </div>
                  <div className="gp-item-body">
                    <p>"{p.texto}"</p>
                  </div>
                  <div className="gp-item-footer" style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                    <button className="btn-gp-edit" onClick={() => handleIniciarEdicion(p)} style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '4px', fontSize: '13px' }}>
                      Editar
                    </button>
                    <button className="btn-gp-delete" onClick={() => handleEliminarPlantilla(p.id)}>
                      Eliminar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  )
}